import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ViewBadgeDialog from '../components/badge/ViewBadgeDialog';
import BadgeCheckDialog from '../components/badge/BadgeCheckDialog';
import BadgeSeriesCheckDialog from '../components/badge/BadgeSeriesCheckDialog';
import BadgeFormDialog from '../components/badge/BadgeFormDialog';
import BadgeSeriesDialog from '../components/badge/BadgeSeriesDialog';
import BadgeSeriesRewardDialog from '../components/badge/BadgeSeriesRewardDialog';
import BadgeSeriesProgressDialog from '../components/badge/BadgeSeriesProgressDialog';
import BadgeTable from '../components/badge/BadgeTable';
import BadgeSeriesList from '../components/badge/BadgeSeriesList';
import ConfirmDeleteDialog from '../components/badge/ConfirmDeleteDialog';
import UserBadgeSeriesProgressDialog from '../components/badge/UserBadgeSeriesProgressDialog';
import BadgeManagementHeader from '../components/badge/BadgeManagementHeader';
import BadgeManagementTabs from '../components/badge/BadgeManagementTabs';
import EmptyBadgesPlaceholder from '../components/badge/EmptyBadgesPlaceholder';
import EmptySeriesPlaceholder from '../components/badge/EmptySeriesPlaceholder';
import BadgeAnalyticsDialog from '../components/badge/BadgeAnalyticsDialog';
// Импортируем утилиты
import {
  getRarityColor,
  initCriteriaByCategory,
  createCompositeCriteria,
  validateCriteria,
  renderCriteriaDescription,
} from '../utils/badgeUtils';
import LoadingIndicator from '../components/badge/LoadingIndicator';
import {
  badgesAPI,
  badgeSeriesAPI,
  coursesAPI,
  achievementsAPI,
  testsAPI,
  challengesAPI,
  usersAPI,
} from '../services/api';

const BadgeManagement = () => {
  const { t } = useTranslation(['badges', 'common']);

  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    badge: null,
  });
  const [confirmDeleteSeries, setConfirmDeleteSeries] = useState({
    open: false,
    series: null,
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentBadge, setCurrentBadge] = useState(null);
  const [imageUploadTab, setImageUploadTab] = useState(0);
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [newBadge, setNewBadge] = useState({
    name: '',
    description: '',
    image: null,
    category: 'achievement',
    rarity: 'common',
    tier: 'bronze',
    isSecret: false,
    criteria: JSON.stringify({ type: 'manual' }),
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Состояние для хранения критериев
  const [badgeCriteria, setBadgeCriteria] = useState({});

  // Состояние для режима редактора критериев (простой/расширенный)
  const [advancedCriteriaMode, setAdvancedCriteriaMode] = useState(false);

  // Состояния для хранения списков доступных элементов для выбора
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableAchievements, setAvailableAchievements] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [availableChallenges, setAvailableChallenges] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Добавляем новые состояния для управления автоматическими проверками
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [checkResult, setCheckResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkHistory, setCheckHistory] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Добавляем новые состояния для аналитики
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState(0);
  const [popularityStats, setPopularityStats] = useState(null);
  const [difficultyStats, setDifficultyStats] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  // Добавляем состояние для просмотра значка
  const [viewBadgeDialogOpen, setViewBadgeDialogOpen] = useState(false);
  const [viewBadge, setViewBadge] = useState(null);
  const [badgeCriteriaDisplay, setBadgeCriteriaDisplay] = useState(null);

  // Состояния для управления сериями значков
  const [badgeSeries, setBadgeSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
  const [seriesDialogMode, setSeriesDialogMode] = useState('create');
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [seriesError, setSeriesError] = useState(null);
  const [seriesFormData, setSeriesFormData] = useState({
    name: '',
    description: '',
    image: null,
    badges: [],
    bonusRewards: {
      experience: 0,
      specialBadge: null,
    },
    requiredBadgesCount: 0,
    active: true,
  });
  const [previewSeriesImage, setPreviewSeriesImage] = useState(null);
  const [badgeSeriesProgress, setBadgeSeriesProgress] = useState(null);
  const [selectedUserSeries, setSelectedUserSeries] = useState(null);
  const [loadingUserSeries, setLoadingUserSeries] = useState(false);
  const [seriesRewardDialogOpen, setSeriesRewardDialogOpen] = useState(false);

  // Состояние для управления вкладками
  const [activeTab, setActiveTab] = useState(0);

  // Добавляем новые состояния для проверки серий значков
  const [seriesCheckDialogOpen, setSeriesCheckDialogOpen] = useState(false);
  const [seriesCheckResult, setSeriesCheckResult] = useState(null);
  const [seriesCheckLoading, setSeriesCheckLoading] = useState(false);
  const [seriesCheckHistory, setSeriesCheckHistory] = useState([]);

  // Загрузка всех значков
  const fetchBadges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await badgesAPI.getAll();
      setBadges(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке значков:', error);
      setError(t('badges:errors.loadBadges'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Удаление значка
  const handleDeleteBadge = async () => {
    if (!confirmDelete.badge) return;

    setError(null); // Сбрасываем ошибку перед операцией
    try {
      await badgesAPI.delete(confirmDelete.badge._id);

      // Обновляем список после удаления
      setBadges(
        badges.filter((badge) => badge._id !== confirmDelete.badge._id)
      );
      setConfirmDelete({ open: false, badge: null });
      // Здесь можно добавить уведомление об успешном удалении
    } catch (error) {
      console.error('Ошибка при удалении значка:', error);
      setError(t('badges:errors.deleteBadge'));
    }
  };

  // Открыть диалог создания нового значка
  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setFormErrors({});
    setError(null); // Сбрасываем ошибку
    setImagePreview('');
    setImageFile(null);
    setImageUploadTab(0);
    setNewBadge({
      name: '',
      description: '',
      image: null,
      category: 'achievement',
      rarity: 'common',
      tier: 'bronze',
      isSecret: false,
      criteria: JSON.stringify({ type: 'manual' }),
    });

    // Инициализируем базовые критерии в зависимости от категории
    const initialCriteria = initCriteriaByCategory('achievement');
    setBadgeCriteria(initialCriteria);

    // Сбрасываем режим на простой при создании нового значка
    setAdvancedCriteriaMode(false);

    // Загружаем списки доступных элементов
    fetchAvailableOptions();
  };

  // Закрыть диалог создания нового значка
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setImagePreview('');
    setImageFile(null);
  };

  // Открыть диалог редактирования значка
  const handleOpenEditDialog = (badge) => {
    // Если badge это ID или объект без criteria, найдем полную информацию из основного списка
    let fullBadge = badge;

    // Если у значка нет критериев, но есть ID, пытаемся найти полную информацию
    if (!badge.criteria && badge._id) {
      const foundBadge = badges.find((b) => b._id === badge._id);
      if (foundBadge) {
        fullBadge = foundBadge;
      }
    }

    setCurrentBadge(fullBadge);
    setFormErrors({});
    setError(null); // Сбрасываем ошибку
    setImagePreview(fullBadge.image);
    setImageFile(null);
    setImageUploadTab(0);
    setEditDialogOpen(true);

    // Парсим criteria из строки, если оно существует
    let criteriaData = {};
    try {
      if (fullBadge.criteria && typeof fullBadge.criteria === 'string') {
        criteriaData = JSON.parse(fullBadge.criteria);

        // Проверяем, являются ли критерии составными (имеют operator и conditions)
        // и устанавливаем соответствующий режим отображения
        if (criteriaData.operator && Array.isArray(criteriaData.conditions)) {
          setAdvancedCriteriaMode(true);
        } else {
          setAdvancedCriteriaMode(false);
        }
      }
    } catch (e) {
      console.error('Ошибка при разборе criteria:', e);
      setAdvancedCriteriaMode(false);
    }

    setBadgeCriteria(criteriaData);

    // Загружаем списки доступных элементов
    fetchAvailableOptions();
  };

  // Закрыть диалог редактирования значка
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentBadge(null);
    setImagePreview('');
    setImageFile(null);
  };

  // Обработка изменений в форме создания значка
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewBadge({
      ...newBadge,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Очистка ошибки поля при изменении
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  // Обработка изменений в форме редактирования значка
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentBadge({
      ...currentBadge,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Очистка ошибки поля при изменении
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  // Обработка переключения вкладок для загрузки изображения
  const handleImageTabChange = (event, newValue) => {
    setImageUploadTab(newValue);
    // Сбрасываем файл при переключении на вкладку URL
    if (newValue === 1) {
      setImageFile(null);
    }
  };

  // Обработка выбора файла через кнопку
  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Обработка изменения категории значка
  const handleCategoryChange = (e, isEdit = false) => {
    const { value } = e.target;

    if (isEdit) {
      setCurrentBadge({
        ...currentBadge,
        category: value,
      });
    } else {
      setNewBadge({
        ...newBadge,
        category: value,
      });
    }

    // Инициализируем базовые критерии в зависимости от категории
    setBadgeCriteria(initCriteriaByCategory(value));

    // Сбрасываем режим на простой при смене категории
    setAdvancedCriteriaMode(false);

    // Сбрасываем ошибки валидации
    if (formErrors.criteria) {
      setFormErrors({
        ...formErrors,
        criteria: undefined,
      });
    }
  };

  // Обработка переключения между простым и расширенным режимом
  const handleToggleAdvancedMode = () => {
    if (!advancedCriteriaMode) {
      // Если переходим в расширенный режим, оборачиваем текущий критерий в составной
      setBadgeCriteria(createCompositeCriteria(badgeCriteria));
    } else {
      // Если возвращаемся в простой режим, берем первое условие из составного (если возможно)
      if (
        badgeCriteria.operator &&
        badgeCriteria.conditions &&
        badgeCriteria.conditions.length > 0
      ) {
        setBadgeCriteria(badgeCriteria.conditions[0]);
      }
    }
    setAdvancedCriteriaMode(!advancedCriteriaMode);
  };

  // Обработка изменений в критериях значка (для простого режима)
  const handleCriteriaChange = (key, value) => {
    if (advancedCriteriaMode) return;

    setBadgeCriteria({
      ...badgeCriteria,
      [key]: value,
    });
  };

  // Обработка изменения типа критерия
  const handleCriteriaTypeChange = (type, index = null) => {
    if (advancedCriteriaMode) {
      // В расширенном режиме меняем тип конкретного условия
      if (index !== null && badgeCriteria.conditions) {
        const newConditions = [...badgeCriteria.conditions];

        // Определяем базовый шаблон для нового типа
        let newCondition = { type };
        switch (type) {
          case 'courseCompletion':
            newCondition = { type, courseCount: 1 };
            break;
          case 'achievementCount':
            newCondition = { type, count: 1 };
            break;
          case 'specificAchievements':
            newCondition = { type, achievementIds: [] };
            break;
          case 'forumActivity':
            newCondition = { type, topicCount: 1, postCount: 5 };
            break;
          case 'streak':
            newCondition = { type, minStreak: 3 };
            break;
          case 'testCompletion':
            newCondition = { type, testIds: [] };
            break;
          case 'seasonalExperience':
            newCondition = { type, season: 'season1', requiredExp: 100 };
            break;
          case 'challengeCompletion':
            newCondition = { type, challengeId: '' };
            break;
          case 'userLevel':
            newCondition = { type, level: 5 };
            break;
          case 'totalExperience':
            newCondition = { type, experience: 1000 };
            break;
          case 'courseCategoryCompletion':
            newCondition = { type, category: 'programming', count: 1 };
            break;
          default:
            newCondition = { type };
        }

        newConditions[index] = newCondition;
        setBadgeCriteria({
          ...badgeCriteria,
          conditions: newConditions,
        });
      }
    } else {
      // В простом режиме просто меняем тип основного условия
      // Определяем базовый шаблон для нового типа (аналогично)
      let newCriteria = { type };
      switch (type) {
        case 'courseCompletion':
          newCriteria = { type, courseCount: 1 };
          break;
        case 'achievementCount':
          newCriteria = { type, count: 1 };
          break;
        case 'specificAchievements':
          newCriteria = { type, achievementIds: [] };
          break;
        case 'forumActivity':
          newCriteria = { type, topicCount: 1, postCount: 5 };
          break;
        case 'streak':
          newCriteria = { type, minStreak: 3 };
          break;
        case 'testCompletion':
          newCriteria = { type, testIds: [] };
          break;
        case 'seasonalExperience':
          newCriteria = { type, season: 'season1', requiredExp: 100 };
          break;
        case 'challengeCompletion':
          newCriteria = { type, challengeId: '' };
          break;
        case 'userLevel':
          newCriteria = { type, level: 5 };
          break;
        case 'totalExperience':
          newCriteria = { type, experience: 1000 };
          break;
        case 'courseCategoryCompletion':
          newCriteria = { type, category: 'programming', count: 1 };
          break;
        default:
          newCriteria = { type };
      }

      setBadgeCriteria(newCriteria);
    }
  };

  // Валидация формы
  const validateForm = (badge) => {
    const errors = {};

    if (!badge.name) errors.name = 'Название обязательно';
    if (!badge.description) errors.description = 'Описание обязательно';

    // Проверка изображения
    if (imageUploadTab === 0 && !imageFile && !badge.image) {
      errors.image = 'Изображение обязательно';
    } else if (imageUploadTab === 1 && !badge.image) {
      errors.image = 'URL изображения обязателен';
    }

    // Проверка критериев
    const criteriaErrors = validateCriteria(badgeCriteria);
    if (criteriaErrors.length > 0) {
      errors.criteria = criteriaErrors.join('. ');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Функция для загрузки изображения на сервер
  const uploadImage = async (file) => {
    try {
      const response = await badgesAPI.uploadImage(file);
      return response.data.image;
    } catch (error) {
      console.error('Ошибка при загрузке изображения:', error);
      throw error;
    }
  };

  // Создание нового значка
  const handleCreateBadge = async () => {
    if (!validateForm(newBadge)) return;

    setSubmitLoading(true);
    setError(null); // Сбрасываем ошибку перед операцией
    try {
      let imageUrl = newBadge.image;

      // Если есть файл для загрузки, загружаем его перед созданием значка
      if (imageUploadTab === 0 && imageFile) {
        try {
          imageUrl = await uploadImage(imageFile);
        } catch (uploadError) {
          setError(t('badges:errors.imageUpload') + ': ' + uploadError.message);
          setSubmitLoading(false);
          return;
        }
      }

      // Создаем объект для отправки на сервер
      const badgeData = {
        name: newBadge.name,
        description: newBadge.description,
        image: imageUrl || '/images/badges/default.png', // Используем загруженное изображение или URL
        category: newBadge.category,
        rarity: newBadge.rarity,
        tier: newBadge.tier,
        isSecret: newBadge.isSecret,
        criteria: JSON.stringify(badgeCriteria), // Используем критерии, заданные пользователем
      };

      const response = await badgesAPI.create(badgeData);
      setBadges([...badges, response.data]);
      handleCloseCreateDialog();
    } catch (error) {
      console.error('Ошибка при создании значка:', error);
      setError(t('badges:errors.createBadge') + ': ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Обновление значка
  const handleUpdateBadge = async () => {
    if (!currentBadge || !validateForm(currentBadge)) return;

    setSubmitLoading(true);
    setError(null); // Сбрасываем ошибку перед операцией
    try {
      let imageUrl = currentBadge.image;

      // Если есть файл для загрузки, загружаем его перед обновлением значка
      if (imageUploadTab === 0 && imageFile) {
        try {
          imageUrl = await uploadImage(imageFile);
        } catch (uploadError) {
          setError(t('badges:errors.imageUpload') + ': ' + uploadError.message);
          setSubmitLoading(false);
          return;
        }
      }

      // Создаем объект для отправки на сервер
      const badgeData = {
        name: currentBadge.name,
        description: currentBadge.description,
        image: imageUrl || '/images/badges/default.png', // Используем загруженное изображение или URL
        category: currentBadge.category,
        rarity: currentBadge.rarity,
        tier: currentBadge.tier,
        isSecret: currentBadge.isSecret,
        criteria: JSON.stringify(badgeCriteria), // Используем критерии, заданные пользователем
      };

      const response = await badgesAPI.update(currentBadge._id, badgeData);
      setBadges(
        badges.map((badge) =>
          badge._id === response.data._id ? response.data : badge
        )
      );
      handleCloseEditDialog();
    } catch (error) {
      console.error('Ошибка при обновлении значка:', error);
      setError(t('badges:errors.updateBadge') + ': ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Функция для загрузки доступных элементов (курсы, достижения, тесты и т.д.)
  const fetchAvailableOptions = async () => {
    setLoadingOptions(true);
    try {
      // Загрузка курсов
      try {
        const coursesResponse = await coursesAPI.getAll({ all: true });

        // Проверяем структуру ответа и извлекаем курсы
        if (
          coursesResponse.data.courses &&
          Array.isArray(coursesResponse.data.courses)
        ) {
          setAvailableCourses(coursesResponse.data.courses);
        } else if (Array.isArray(coursesResponse.data)) {
          setAvailableCourses(coursesResponse.data);
        } else {
          console.error(
            'Неожиданная структура данных курсов:',
            coursesResponse.data
          );
          setAvailableCourses([]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке курсов:', error);
        setAvailableCourses([]);
      }

      // Загрузка достижений
      try {
        const achievementsResponse = await achievementsAPI.getAll({
          all: true,
        });

        // Проверяем структуру ответа и извлекаем достижения
        if (
          achievementsResponse.data.achievements &&
          Array.isArray(achievementsResponse.data.achievements)
        ) {
          setAvailableAchievements(achievementsResponse.data.achievements);
        } else if (Array.isArray(achievementsResponse.data)) {
          setAvailableAchievements(achievementsResponse.data);
        } else {
          console.error(
            'Неожиданная структура данных достижений:',
            achievementsResponse.data
          );
          setAvailableAchievements([]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке достижений:', error);
        setAvailableAchievements([]);
      }

      // Загрузка тестов
      try {
        const testsResponse = await testsAPI.getAll();
        setAvailableTests(testsResponse.data);
      } catch (error) {
        console.error('Ошибка при загрузке тестов:', error);
        setAvailableTests([]);
      }

      // Загрузка испытаний
      try {
        const challengesResponse = await challengesAPI.getAll();
        setAvailableChallenges(challengesResponse.data);
      } catch (error) {
        console.error('Ошибка при загрузке испытаний:', error);
        setAvailableChallenges([]);
      }
    } catch (error) {
      console.error('Ошибка при загрузке списков:', error);
      setError(t('badges:errors.loadOptions'));
    } finally {
      setLoadingOptions(false);
    }
  };

    // Загрузка серий значков
  const fetchBadgeSeries = useCallback(async () => {
    setLoadingSeries(true);
    setSeriesError(null);
    try {
      const response = await badgeSeriesAPI.getAll();
      setBadgeSeries(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке серий значков:', error);
      setSeriesError(t('badges:errors.loadSeries'));
    } finally {
      setLoadingSeries(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBadges();
    fetchBadgeSeries();
  }, [fetchBadges, fetchBadgeSeries]);

  // Обработка изменения параметра условия в расширенном режиме
  const handleAdvancedCriteriaParamChange = (index, paramKey, value) => {
    if (!advancedCriteriaMode || !badgeCriteria.conditions) return;

    const newConditions = [...badgeCriteria.conditions];
    newConditions[index] = {
      ...newConditions[index],
      [paramKey]: value,
    };

    setBadgeCriteria({
      ...badgeCriteria,
      conditions: newConditions,
    });
  };

  // Обработка изменения логического оператора
  const handleOperatorChange = (operator) => {
    if (!advancedCriteriaMode) return;

    setBadgeCriteria({
      ...badgeCriteria,
      operator,
    });
  };

  // Добавление нового условия в составной критерий
  const handleAddCondition = () => {
    if (!advancedCriteriaMode || !badgeCriteria.conditions) return;

    // Добавляем базовое условие в зависимости от категории значка
    const category = currentBadge ? currentBadge.category : newBadge.category;
    let newCondition = initCriteriaByCategory(category);

    setBadgeCriteria({
      ...badgeCriteria,
      conditions: [...badgeCriteria.conditions, newCondition],
    });
  };

  // Обработка удаления условия из составного критерия
  const handleRemoveCondition = (index) => {
    if (!advancedCriteriaMode || !badgeCriteria.conditions) return;

    // Не удаляем, если осталось только одно условие
    if (badgeCriteria.conditions.length <= 1) return;

    const newConditions = badgeCriteria.conditions.filter(
      (_, i) => i !== index
    );
    setBadgeCriteria({
      ...badgeCriteria,
      conditions: newConditions,
    });
  };

  // Функция для загрузки списка пользователей
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const response = await usersAPI.getAll({ all: true });

      if (!response.data) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      setAvailableUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
      setError(t('badges:errors.loadUsers') + ': ' + error.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Функция для запуска проверки значков
  const handleRunCheck = async () => {
    setCheckLoading(true);
    setError(null); // Сбрасываем ошибку перед операцией
    try {
      const response = await badgesAPI.runCheck({
        userId: selectedUser ? selectedUser._id : undefined,
        dryRun,
      });

      setCheckResult(response.data.result);

      // Добавляем результат в историю проверок
      setCheckHistory(
        [
          {
            timestamp: new Date().toISOString(),
            result: response.data.result,
            userId: selectedUser ? selectedUser._id : 'all',
            userName: selectedUser
              ? selectedUser.fullName
              : t('badges:badgeManagement.check.allUsers'),
            dryRun,
          },
          ...checkHistory,
        ].slice(0, 10)
      ); // Храним максимум 10 последних проверок

      if (!dryRun) {
        // Обновляем список значков, если была реальная выдача
        fetchBadges();
      }
    } catch (error) {
      console.error('Ошибка при проверке значков:', error);
      setError(t('badges:errors.checkBadges') + ': ' + error.message);
    } finally {
      setCheckLoading(false);
    }
  };

  // Открыть диалог проверки значков
  const handleOpenCheckDialog = () => {
    setCheckDialogOpen(true);
    setCheckResult(null);
    setSelectedUser(null);
    setDryRun(true);
    setError(null); // Сбрасываем ошибку
    // Загружаем список пользователей при открытии диалога
    fetchUsers();
  };

  // Закрыть диалог проверки значков
  const handleCloseCheckDialog = () => {
    setCheckDialogOpen(false);
    setError(null); // Сбрасываем ошибку при закрытии диалога
  };

  // Форматирование времени
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Функция для получения статистики популярности значков
  const fetchPopularityStats = async () => {
    setLoadingAnalytics(true);
    setAnalyticsError(null);
    try {
      const response = await badgesAPI.getPopularityStats();
      setPopularityStats(response.data);
    } catch (error) {
      console.error('Ошибка при получении статистики популярности:', error);
      setAnalyticsError(
        t('badges:errors.loadAnalytics') + ': ' + error.message
      );
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Функция для получения статистики сложности значков
  const fetchDifficultyStats = async () => {
    setLoadingAnalytics(true);
    setAnalyticsError(null);
    try {
      const response = await badgesAPI.getDifficultyStats();
      setDifficultyStats(response.data);
    } catch (error) {
      console.error('Ошибка при получении статистики сложности:', error);
      setAnalyticsError(
        t('badges:errors.loadAnalytics') + ': ' + error.message
      );
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Открыть диалог аналитики
  const handleOpenAnalyticsDialog = () => {
    setAnalyticsDialogOpen(true);
    setAnalyticsTab(0);
    setAnalyticsError(null); // Сбрасываем ошибку аналитики
    fetchPopularityStats();
  };

  // Закрыть диалог аналитики
  const handleCloseAnalyticsDialog = () => {
    setAnalyticsDialogOpen(false);
  };

  // Обработка изменения вкладок аналитики
  const handleAnalyticsTabChange = (event, newValue) => {
    setAnalyticsTab(newValue);
    if (newValue === 0 && !popularityStats) {
      fetchPopularityStats();
    } else if (newValue === 1 && !difficultyStats) {
      fetchDifficultyStats();
    }
  };

  // Функция для открытия диалога просмотра значка
  const handleOpenViewBadgeDialog = (badge) => {
    // Если badge это ID или объект без criteria, найдем полную информацию из основного списка
    let fullBadge = badge;

    // Если у значка нет критериев, но есть ID, пытаемся найти полную информацию
    if (!badge.criteria && badge._id) {
      const foundBadge = badges.find((b) => b._id === badge._id);
      if (foundBadge) {
        fullBadge = foundBadge;
      }
    }

    setViewBadge(fullBadge);

    // Пытаемся распарсить критерии значка
    try {
      if (fullBadge.criteria) {
        const criteriaData = JSON.parse(fullBadge.criteria);
        setBadgeCriteriaDisplay(criteriaData);
      } else {
        setBadgeCriteriaDisplay(null);
      }
    } catch (e) {
      console.error('Ошибка при разборе критериев:', e);
      setBadgeCriteriaDisplay(null);
    }

    setViewBadgeDialogOpen(true);
  };

  // Функция для закрытия диалога просмотра значка
  const handleCloseViewBadgeDialog = () => {
    setViewBadgeDialogOpen(false);
    setViewBadge(null);
    setBadgeCriteriaDisplay(null);
  };

  // Открытие диалога серии значков
  const handleOpenSeriesDialog = (mode, series = null) => {
    setSeriesDialogMode(mode);
    setSeriesDialogOpen(true);
    setSeriesError(null); // Сбрасываем ошибку серии

    // Очищаем все ошибки формы, связанные с серией
    const newFormErrors = { ...formErrors };
    Object.keys(newFormErrors).forEach((key) => {
      if (key.startsWith('series_')) {
        delete newFormErrors[key];
      }
    });
    setFormErrors(newFormErrors);

    if (mode === 'edit' && series) {
      setSelectedSeries(series);
      setSeriesFormData({
        name: series.name,
        description: series.description,
        image: series.image,
        badges: series.badges || [],
        bonusRewards: series.bonusRewards || {
          experience: 0,
          specialBadge: null,
        },
        requiredBadgesCount: series.requiredBadgesCount || 0,
        active: series.active !== false,
      });
      setPreviewSeriesImage(series.image);
    } else {
      setSelectedSeries(null);
      setSeriesFormData({
        name: '',
        description: '',
        image: null,
        badges: [],
        bonusRewards: {
          experience: 0,
          specialBadge: null,
        },
        requiredBadgesCount: 0,
        active: true,
      });
      setPreviewSeriesImage(null);
    }
  };

  // Закрытие диалога серии значков
  const handleCloseSeriesDialog = () => {
    setSeriesDialogOpen(false);
    setSelectedSeries(null);
    setPreviewSeriesImage(null);
    setSeriesError(null); // Сбрасываем ошибку серии при закрытии

    // Очищаем все ошибки формы, связанные с серией
    const newFormErrors = { ...formErrors };
    Object.keys(newFormErrors).forEach((key) => {
      if (key.startsWith('series_')) {
        delete newFormErrors[key];
      }
    });
    setFormErrors(newFormErrors);
  };

  // Обработка изменений в форме серии
  const handleSeriesFormChange = (field, value) => {
    setSeriesFormData({
      ...seriesFormData,
      [field]: value,
    });

    // Очистка ошибки поля при изменении
    if (formErrors[`series_${field}`]) {
      setFormErrors({
        ...formErrors,
        [`series_${field}`]: '',
      });
    }
  };

  // Обработка изменений в бонусных наградах
  const handleBonusRewardChange = (field, value) => {
    setSeriesFormData({
      ...seriesFormData,
      bonusRewards: {
        ...seriesFormData.bonusRewards,
        [field]: value,
      },
    });
  };

  // Валидация формы серии
  const validateSeriesForm = () => {
    const errors = {};

    if (!seriesFormData.name) errors.series_name = 'Название обязательно';
    if (!seriesFormData.description)
      errors.series_description = 'Описание обязательно';
    if (seriesFormData.badges.length === 0)
      errors.series_badges = 'Выберите хотя бы один значок';
    if (seriesFormData.requiredBadgesCount <= 0) {
      errors.series_requiredBadgesCount = 'Количество должно быть больше 0';
    } else if (
      seriesFormData.requiredBadgesCount > seriesFormData.badges.length
    ) {
      errors.series_requiredBadgesCount =
        'Не может быть больше количества значков в серии';
    }

    setFormErrors({ ...formErrors, ...errors });
    return Object.keys(errors).length === 0;
  };

  // Создание новой серии значков
  const handleCreateSeries = async () => {
    if (!validateSeriesForm()) return;

    setSubmitLoading(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      let imageUrl = seriesFormData.image;

      // Если есть файл для загрузки, загружаем его
      if (seriesFormData.imageFile) {
        try {
          const uploadResponse = await badgeSeriesAPI.uploadImage(
            seriesFormData.imageFile
          );
          imageUrl = uploadResponse.data.image;
        } catch (error) {
          throw new Error('Не удалось загрузить изображение');
        }
      }

      const seriesData = {
        ...seriesFormData,
        image: imageUrl,
        imageFile: undefined, // Не отправляем файл
      };

      const response = await badgeSeriesAPI.create(seriesData);
      setBadgeSeries([...badgeSeries, response.data]);
      handleCloseSeriesDialog();
    } catch (error) {
      console.error('Ошибка при создании серии значков:', error);
      setSeriesError(t('badges:errors.createSeries') + ': ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Обновление серии значков
  const handleUpdateSeries = async () => {
    if (!selectedSeries || !validateSeriesForm()) return;

    setSubmitLoading(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      let imageUrl = seriesFormData.image;

      // Если есть файл для загрузки, загружаем его
      if (seriesFormData.imageFile) {
        try {
          const uploadResponse = await badgeSeriesAPI.uploadImage(
            seriesFormData.imageFile
          );
          imageUrl = uploadResponse.data.image;
        } catch (error) {
          throw new Error('Не удалось загрузить изображение');
        }
      }

      const seriesData = {
        ...seriesFormData,
        image: imageUrl,
        imageFile: undefined, // Не отправляем файл
      };

      const response = await badgeSeriesAPI.update(
        selectedSeries._id,
        seriesData
      );
      setBadgeSeries(
        badgeSeries.map((series) =>
          series._id === response.data._id ? response.data : series
        )
      );
      handleCloseSeriesDialog();
    } catch (error) {
      console.error('Ошибка при обновлении серии значков:', error);
      setSeriesError(t('badges:errors.updateSeries') + ': ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Удаление серии значков
  const handleDeleteSeries = async (seriesId) => {
    if (!seriesId) return;

    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      await badgeSeriesAPI.delete(seriesId);

      // Обновляем список после удаления
      setBadgeSeries(badgeSeries.filter((series) => series._id !== seriesId));
    } catch (error) {
      console.error('Ошибка при удалении серии значков:', error);
      setSeriesError(t('badges:errors.deleteSeries') + ': ' + error.message);
    }
  };

  // Функция для просмотра прогресса пользователей в серии
  const handleViewSeriesProgress = async (seriesId) => {
    if (!seriesId) {
      setSeriesError('Идентификатор серии не указан');
      return;
    }

    setLoadingUserSeries(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      const response = await badgeSeriesAPI.getProgress(seriesId);

      if (!response.data || !response.data.series) {
        throw new Error('Данные о серии не получены');
      }

      setBadgeSeriesProgress(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке прогресса серии:', error);
      setSeriesError(
        `Не удалось загрузить прогресс пользователей: ${error.message}`
      );
      // Показываем ошибку пользователю
      alert(`Ошибка при получении прогресса серии: ${error.message}`);
    } finally {
      setLoadingUserSeries(false);
    }
  };

  // Функция для просмотра прогресса конкретного пользователя
  const handleViewUserSeriesProgress = async (userId) => {
    setLoadingUserSeries(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      const response = await badgeSeriesAPI.getUserProgress(userId);
      setSelectedUserSeries(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке серий пользователя:', error);
      setSeriesError(
        'Не удалось загрузить данные пользователя: ' + error.message
      );
    } finally {
      setLoadingUserSeries(false);
    }
  };

  // Открыть диалог награды за серию
  const handleOpenSeriesRewardDialog = (series) => {
    setSelectedSeries(series);
    setSeriesRewardDialogOpen(true);
    setSeriesError(null); // Сбрасываем ошибку серии
    fetchUsers();
  };

  // Закрыть диалог награды за серию
  const handleCloseSeriesRewardDialog = () => {
    setSeriesRewardDialogOpen(false);
    setSelectedSeries(null);
    setSelectedUser(null); // Сбрасываем выбранного пользователя при закрытии
    setSeriesError(null); // Сбрасываем ошибку серии при закрытии
  };

  // Выдача награды за серию пользователю
  const handleAwardSeriesReward = async (userId, seriesId) => {
    setSubmitLoading(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      const response = await badgeSeriesAPI.awardSeriesReward(seriesId, userId);

      // Обновляем прогресс после выдачи
      handleViewSeriesProgress(seriesId);

      return response.data;
    } catch (error) {
      console.error('Ошибка при выдаче награды за серию:', error);
      setSeriesError('Не удалось выдать награду: ' + error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Загружаем серии значков при загрузке компонента
  useEffect(() => {
    fetchBadges();
    fetchBadgeSeries();
  }, [fetchBadges, fetchBadgeSeries]);

  // Функция для запуска проверки серий значков
  const handleRunSeriesCheck = async () => {
    setSeriesCheckLoading(true);
    setSeriesError(null); // Сбрасываем ошибку серии перед операцией
    try {
      const response = await badgeSeriesAPI.runSeriesCheck({
        userId: selectedUser ? selectedUser._id : undefined,
        dryRun,
      });

      setSeriesCheckResult(response.data.result);

      // Добавляем результат в историю проверок
      setSeriesCheckHistory(
        [
          {
            timestamp: new Date().toISOString(),
            result: response.data.result,
            userId: selectedUser ? selectedUser._id : 'all',
            userName: selectedUser
              ? selectedUser.fullName
              : t('badges:badgeManagement.check.allUsers'),
            dryRun,
          },
          ...seriesCheckHistory,
        ].slice(0, 10)
      ); // Храним максимум 10 последних проверок

      if (!dryRun) {
        // Обновляем список серий и значков, если была реальная выдача
        fetchBadges();
        fetchBadgeSeries();
      }
    } catch (error) {
      console.error('Ошибка при проверке серий значков:', error);
      setSeriesError(
        'Не удалось выполнить проверку серий значков: ' + error.message
      );
    } finally {
      setSeriesCheckLoading(false);
    }
  };

  // Открыть диалог проверки серий значков
  const handleOpenSeriesCheckDialog = () => {
    setSeriesCheckDialogOpen(true);
    setSeriesCheckResult(null);
    setSelectedUser(null);
    setDryRun(true);
    setSeriesError(null); // Сбрасываем ошибку серии
    // Загружаем список пользователей при открытии диалога
    fetchUsers();
  };

  // Закрыть диалог проверки серий значков
  const handleCloseSeriesCheckDialog = () => {
    setSeriesCheckDialogOpen(false);
    setSeriesError(null); // Сбрасываем ошибку серии при закрытии
  };

  // Обработчик подтверждения удаления серии
  const handleConfirmDeleteSeries = async () => {
    if (!confirmDeleteSeries.series) return;

    try {
      await handleDeleteSeries(confirmDeleteSeries.series._id);
      setConfirmDeleteSeries({ open: false, series: null });
    } catch (error) {
      console.error('Ошибка при удалении серии значков:', error);
      setSeriesError(t('badges:errors.deleteSeries') + ': ' + error.message);
    }
  };

  if (loading || loadingSeries) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <BadgeManagementHeader
        activeTab={activeTab}
        handleOpenAnalyticsDialog={handleOpenAnalyticsDialog}
        handleOpenCheckDialog={handleOpenCheckDialog}
        handleOpenSeriesCheckDialog={handleOpenSeriesCheckDialog}
        handleOpenCreateDialog={handleOpenCreateDialog}
        handleOpenSeriesDialog={handleOpenSeriesDialog}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {seriesError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {seriesError}
        </Alert>
      )}

      <BadgeManagementTabs
        activeTab={activeTab}
        handleTabChange={(e, newValue) => setActiveTab(newValue)}
      />

      {/* Вкладка со значками */}
      {activeTab === 0 && (
        <Box>
          {!loading && badges.length === 0 ? (
            <EmptyBadgesPlaceholder />
          ) : (
            <BadgeTable
              badges={badges}
              handleOpenViewBadgeDialog={handleOpenViewBadgeDialog}
              handleOpenEditDialog={handleOpenEditDialog}
              setConfirmDelete={setConfirmDelete}
              getRarityColor={getRarityColor}
            />
          )}
        </Box>
      )}

      {/* Вкладка с сериями значков */}
      {activeTab === 1 && (
        <Box>
          {loadingSeries ? (
            <LoadingIndicator height="200px" />
          ) : badgeSeries.length === 0 ? (
            <EmptySeriesPlaceholder />
          ) : (
            <BadgeSeriesList
              badgeSeries={badgeSeries}
              badges={badges}
              handleOpenViewBadgeDialog={handleOpenViewBadgeDialog}
              handleViewSeriesProgress={handleViewSeriesProgress}
              handleOpenSeriesRewardDialog={handleOpenSeriesRewardDialog}
              handleOpenSeriesDialog={handleOpenSeriesDialog}
              setConfirmDeleteSeries={setConfirmDeleteSeries}
              getRarityColor={getRarityColor}
            />
          )}
        </Box>
      )}

      {/* Диалог подтверждения удаления */}
      <ConfirmDeleteDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, badge: null })}
        badge={confirmDelete.badge}
        handleDeleteBadge={handleDeleteBadge}
        title={t('badges:badgeManagement.confirmDelete.title')}
        contentText={
          confirmDelete.badge ? t('badges:badgeManagement.confirmDelete.message') : ''
        }
      />

      {/* Диалог создания нового значка */}
      <BadgeFormDialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        title={t('badges:badgeManagement.forms.createBadge')}
        badge={newBadge}
        formErrors={formErrors}
        submitLoading={submitLoading}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
        imageFile={imageFile}
        setImageFile={setImageFile}
        imageUploadTab={imageUploadTab}
        setImageUploadTab={setImageUploadTab}
        badgeCriteria={badgeCriteria}
        advancedCriteriaMode={advancedCriteriaMode}
        handleInputChange={handleInputChange}
        handleCategoryChange={handleCategoryChange}
        handleToggleAdvancedMode={handleToggleAdvancedMode}
        handleOperatorChange={handleOperatorChange}
        handleCriteriaChange={handleCriteriaChange}
        handleCriteriaTypeChange={handleCriteriaTypeChange}
        handleAdvancedCriteriaParamChange={handleAdvancedCriteriaParamChange}
        handleAddCondition={handleAddCondition}
        handleRemoveCondition={handleRemoveCondition}
        handleSave={handleCreateBadge}
        availableCourses={availableCourses}
        availableAchievements={availableAchievements}
        availableTests={availableTests}
        availableChallenges={availableChallenges}
        loadingOptions={loadingOptions}
        getRarityColor={getRarityColor}
      />

      {/* Диалог редактирования значка */}
      <BadgeFormDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        title={t('badges:badgeManagement.forms.editBadge')}
        badge={currentBadge || {}}
        formErrors={formErrors}
        submitLoading={submitLoading}
        imagePreview={imagePreview}
        setImagePreview={setImagePreview}
        imageFile={imageFile}
        setImageFile={setImageFile}
        imageUploadTab={imageUploadTab}
        setImageUploadTab={setImageUploadTab}
        badgeCriteria={badgeCriteria}
        advancedCriteriaMode={advancedCriteriaMode}
        handleInputChange={handleEditInputChange}
        handleCategoryChange={handleCategoryChange}
        handleToggleAdvancedMode={handleToggleAdvancedMode}
        handleOperatorChange={handleOperatorChange}
        handleCriteriaChange={handleCriteriaChange}
        handleCriteriaTypeChange={handleCriteriaTypeChange}
        handleAdvancedCriteriaParamChange={handleAdvancedCriteriaParamChange}
        handleAddCondition={handleAddCondition}
        handleRemoveCondition={handleRemoveCondition}
        handleSave={handleUpdateBadge}
        availableCourses={availableCourses}
        availableAchievements={availableAchievements}
        availableTests={availableTests}
        availableChallenges={availableChallenges}
        loadingOptions={loadingOptions}
        getRarityColor={getRarityColor}
      />

      {/* Диалог просмотра значка */}
      <ViewBadgeDialog
        open={viewBadgeDialogOpen}
        onClose={handleCloseViewBadgeDialog}
        badge={viewBadge}
        criteriaDisplay={badgeCriteriaDisplay}
        handleOpenEdit={(badge) => {
          handleCloseViewBadgeDialog();
          handleOpenEditDialog(badge);
        }}
        getRarityColor={getRarityColor}
        renderCriteriaDescription={renderCriteriaDescription}
      />

      {/* Диалог серии значков */}
      <BadgeSeriesDialog
        open={seriesDialogOpen}
        onClose={handleCloseSeriesDialog}
        mode={seriesDialogMode}
        seriesFormData={seriesFormData}
        formErrors={formErrors}
        submitLoading={submitLoading}
        previewSeriesImage={previewSeriesImage}
        setPreviewSeriesImage={setPreviewSeriesImage}
        imageUploadTab={imageUploadTab}
        handleImageTabChange={handleImageTabChange}
        fileInputRef={fileInputRef}
        handleChooseFile={handleChooseFile}
        handleSeriesFormChange={handleSeriesFormChange}
        handleBonusRewardChange={handleBonusRewardChange}
        handleSave={
          seriesDialogMode === 'create'
            ? handleCreateSeries
            : handleUpdateSeries
        }
        badges={badges}
      />

      {/* Диалог награды за серию */}
      <BadgeSeriesRewardDialog
        open={seriesRewardDialogOpen}
        onClose={handleCloseSeriesRewardDialog}
        selectedSeries={selectedSeries}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        availableUsers={availableUsers}
        loadingUsers={loadingUsers}
        submitLoading={submitLoading}
        handleAwardSeriesReward={handleAwardSeriesReward}
      />

      {/* Диалог прогресса по серии значков */}
      <BadgeSeriesProgressDialog
        open={!!badgeSeriesProgress}
        onClose={() => setBadgeSeriesProgress(null)}
        badgeSeriesProgress={badgeSeriesProgress}
        handleViewUserSeriesProgress={handleViewUserSeriesProgress}
        handleAwardSeriesReward={handleAwardSeriesReward}
        submitLoading={submitLoading}
        getRarityColor={getRarityColor}
      />

      {/* Диалог серий значков пользователя */}
      <UserBadgeSeriesProgressDialog
        open={!!selectedUserSeries}
        onClose={() => setSelectedUserSeries(null)}
        selectedUserSeries={selectedUserSeries}
        loadingUserSeries={loadingUserSeries}
        handleAwardSeriesReward={handleAwardSeriesReward}
        badges={badges}
        getRarityColor={getRarityColor}
        refreshUserSeriesProgress={
          selectedUserSeries && selectedUserSeries.user
            ? () => handleViewUserSeriesProgress(selectedUserSeries.user._id)
            : undefined
        }
      />

      {/* Диалог проверки серий значков */}
      <BadgeSeriesCheckDialog
        open={seriesCheckDialogOpen}
        onClose={handleCloseSeriesCheckDialog}
        loadingUsers={loadingUsers}
        availableUsers={availableUsers}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        dryRun={dryRun}
        setDryRun={setDryRun}
        seriesCheckResult={seriesCheckResult}
        seriesCheckLoading={seriesCheckLoading}
        seriesCheckHistory={seriesCheckHistory}
        handleRunSeriesCheck={handleRunSeriesCheck}
        formatDateTime={formatDateTime}
        error={seriesError}
      />

      {/* Диалог подтверждения удаления серии */}
      <ConfirmDeleteDialog
        open={confirmDeleteSeries.open}
        onClose={() => setConfirmDeleteSeries({ open: false, series: null })}
        series={confirmDeleteSeries.series}
        handleDeleteSeries={handleConfirmDeleteSeries}
        title={t('badges:badgeManagement.confirmDelete.seriesTitle')}
        contentText={
          confirmDeleteSeries.series
            ? t('badges:badgeManagement.confirmDelete.seriesMessage')
            : ''
        }
      />

      {/* Диалог аналитики значков */}
      <BadgeAnalyticsDialog
        open={analyticsDialogOpen}
        onClose={handleCloseAnalyticsDialog}
        analyticsTab={analyticsTab}
        handleAnalyticsTabChange={handleAnalyticsTabChange}
        popularityStats={popularityStats}
        difficultyStats={difficultyStats}
        loadingAnalytics={loadingAnalytics}
        analyticsError={analyticsError}
        getRarityColor={getRarityColor}
      />

      {/* Диалог проверки значков */}
      <BadgeCheckDialog
        open={checkDialogOpen}
        onClose={handleCloseCheckDialog}
        loadingUsers={loadingUsers}
        availableUsers={availableUsers}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        dryRun={dryRun}
        setDryRun={setDryRun}
        checkResult={checkResult}
        checkLoading={checkLoading}
        checkHistory={checkHistory}
        handleRunCheck={handleRunCheck}
        formatDateTime={formatDateTime}
        error={error}
      />
    </Box>
  );
};

export default BadgeManagement;
